import { ReactNode, useMemo } from 'react';

import { usePromptContext } from '@/store/prompts';
import type { PromptContextSubject } from '@/types/prompts';

import { PromptSubjectContext, toSubjectValue } from './usePromptSubject';

/**
 * A chat wraps its composer in this with its subject: the Job in Job chat,
 * the Library Document in Ask Tia, the Quote Document in Ask Salma, none
 * elsewhere. It runs the lookup when the chat opens and again when the
 * subject changes (one cached call per subject); a chip already in the
 * composer keeps the values it was filled with.
 *
 * Nothing here blocks anything: with the switch off there is no call, and a
 * pending or failed lookup reads as nothing found.
 */
export function PromptSubjectProvider({
  subject,
  children,
}: {
  subject: PromptContextSubject | null;
  children: ReactNode;
}) {
  const { context, isFetching, isError } = usePromptContext(subject);
  const kind = subject?.kind;
  const id = subject?.id;
  const value = useMemo(
    () => toSubjectValue(subject, context, isFetching, isError),
    // The subject is read by kind and id, not by object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kind, id, context, isFetching, isError]
  );
  return (
    <PromptSubjectContext.Provider value={value}>
      {children}
    </PromptSubjectContext.Provider>
  );
}
